import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import Login from '../pages/Login';
import ForcePasswordChangeModal from './modals/ForcePasswordChangeModal';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute - Guards authenticated routes
 *
 * Flow:
 * 1. If loading, show loading spinner
 * 2. If not authenticated, show login page
 * 3. If authenticated but must_change_password is true, show password change modal
 * 4. Otherwise, render children (dashboard)
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user, refreshUser } = useAuth();

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login
  if (!isAuthenticated) {
    return <Login />;
  }

  // Handle password change success
  const handlePasswordChangeSuccess = async () => {
    // Refresh user data to update must_change_password flag
    await refreshUser();
  };

  // Authenticated but needs to change password - show modal over dashboard (dimmed)
  // This ensures the modal is clearly blocking access while showing context
  if (user?.must_change_password) {
    return (
      <>
        {/* Render dashboard in background (dimmed by modal overlay) */}
        <div className="pointer-events-none opacity-30">
          {children}
        </div>
        {/* Force password change modal (non-dismissible) */}
        <ForcePasswordChangeModal
          isOpen={true}
          onSuccess={handlePasswordChangeSuccess}
          userEmail={user?.email}
        />
      </>
    );
  }

  // Fully authenticated - render dashboard
  return <>{children}</>;
};

export default ProtectedRoute;
